import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames/bind';
import { get, isEmpty, keyBy } from 'lodash';
import PropTypes from 'prop-types';
import { useCurrentValueGetter } from 'hooks';
import { ClearButton } from 'modules/campaigns/modules/campaigns-reports/components/Toolbar';
import { useSelectedReportId } from 'modules/campaigns/modules/campaigns-reports/hooks';
import { useReportFilters } from 'modules/campaigns/modules/campaigns-reports/hooks/useReportFilters';
import { useReportFiltersStatus } from 'modules/campaigns/modules/campaigns-reports/hooks/useReportFiltersStatus';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { STATIC_FILTERS_MAP, FETCHED_VALUES_FILTER_MAP } from '../../config';
import { FiltersToolbar } from './FiltersToolbar/FiltersToolbar';
import { emptyValueOptions, generateStaticFilteredOptions, PAGE_SIZE } from './filterUtils';
import useConfigFilters from './hooks/useConfigFilters';
import styles from './filters.module.scss';

const classNameBuilder = classnames.bind(styles);

export const Filters = ({ reportConfig, getApplyBtnMetricsAttrs }) => {
    const [isAddingNewFilter, setIsAddingNewFilter] = useState(false);
    const { formatMessage } = useIntl();
    const { permittedFilters, requiredFiltersSet } = useConfigFilters(reportConfig);
    const [editingFilterIndex, setEditingFilterIndex] = useState(-1);
    const [selectedReport] = useSelectedReportId();
    const { accountId } = useAccount();
    const [isFilterActive, setFilterActive] = useReportFiltersStatus();
    const { reportFilters, addReportFilter, updateReportFilter, removeReportFilter, clearReportFilters } =
        useReportFilters();
    const reportFiltersGetter = useCurrentValueGetter(reportFilters);

    const stopEditing = useCallback(() => setEditingFilterIndex(-1), []);
    const startAddingNewFilter = useCallback(() => setIsAddingNewFilter(true), []);
    const stopAddingNewFilter = useCallback(() => setIsAddingNewFilter(false), []);

    const addFilter = filter => {
        stopAddingNewFilter();
        addReportFilter(filter);
        setFilterActive(true);
    };
    const updateFilter = (index, filter) => {
        updateReportFilter(index, filter);
        stopAddingNewFilter();
        stopEditing();
    };
    const removeFilter = filter => {
        if (reportFilters.length === 1) {
            setFilterActive(false);
        }
        removeReportFilter(filter);
    };
    const clearFilters = () => {
        stopAddingNewFilter();
        setFilterActive(false);
        clearReportFilters();
    };

    const safeFetching = (fetcherObj, params) => {
        if (!fetcherObj) {
            return emptyValueOptions;
        }

        const { generateOptions } = params;
        const { fetcher, messagePrefix, responseTransformer, argsNames = [] } = fetcherObj;
        const responseHandler = data =>
            messagePrefix
                ? generateOptions(messagePrefix, responseTransformer(data, params))
                : responseTransformer(data, params);

        const args = argsNames.map(name => params[name]);

        return fetcher(...args)
            .then(data => responseHandler(data))
            .catch(() => emptyValueOptions);
    };
    const reportFiltersMap = useMemo(() => keyBy(reportConfig?.filters, 'id'), [reportConfig]);
    const getValueOptions = (type, searchInput, page) => {
        const referencedFilterType = get(reportFiltersMap, `${type}.referencedFilterType`);
        const generateOptions = (messageIdPrefix, values) =>
            generateStaticFilteredOptions({ formatMessage, messageIdPrefix, values, searchInput });
        const staticTypeMap = STATIC_FILTERS_MAP[type] ?? STATIC_FILTERS_MAP[referencedFilterType];
        const filterMap = keyBy(reportConfig.filters, 'id');
        const options = get(filterMap, `${type}.options`);

        if (options) {
            return { options, hasMore: false };
        }

        if (staticTypeMap) {
            const { messageIdPrefix, values } = staticTypeMap;
            return generateOptions(messageIdPrefix, values);
        }

        const fetchingConfig = FETCHED_VALUES_FILTER_MAP[type] ?? FETCHED_VALUES_FILTER_MAP[referencedFilterType];

        return safeFetching(fetchingConfig, {
            generateOptions,
            searchInput,
            page,
            accountId,
            pageSize: PAGE_SIZE,
        });
    };

    useEffect(() => {
        if (isEmpty(reportFilters) && isFilterActive) {
            setFilterActive(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportFilters]);

    useEffect(() => {
        stopAddingNewFilter();
        stopEditing();
    }, [selectedReport, stopAddingNewFilter, stopEditing]);

    useEffect(() => {
        if (!isFilterActive) {
            stopAddingNewFilter();
            return;
        }

        if (isEmpty(reportFiltersGetter())) {
            startAddingNewFilter();
        }
    }, [isFilterActive, reportFiltersGetter, startAddingNewFilter, stopAddingNewFilter]);

    if (!isFilterActive) {
        return null;
    }

    return (
        <div className={styles['toolbar']}>
            <div className={styles['filters-label']}>
                <FormattedMessage id="app.campaigns.reports.filters.toolbar.title" defaultMessage="Filters" />
            </div>
            <FiltersToolbar
                filters={reportFilters}
                addFilter={addFilter}
                updateFilter={updateFilter}
                removeFilter={removeFilter}
                editingFilterIndex={editingFilterIndex}
                setEditingFilterIndex={setEditingFilterIndex}
                stopEditing={stopEditing}
                isAddingNewFilter={isAddingNewFilter}
                startAddingNewFilter={startAddingNewFilter}
                stopAddingNewFilter={stopAddingNewFilter}
                availableFilters={permittedFilters}
                getValueOptions={getValueOptions}
                getApplyBtnMetricsAttrs={getApplyBtnMetricsAttrs}
                reportConfig={reportConfig}
            />
            {requiredFiltersSet.size === 0 && (
                <ClearButton onClick={clearFilters} className={classNameBuilder('clear-button')}>
                    <FormattedMessage
                        id="app.campaigns.reports.filters.toolbar.buttons.clearAll"
                        defaultMessage="Clear All"
                    />
                </ClearButton>
            )}
        </div>
    );
};

Filters.propTypes = {
    /** return value is spread into apply button for metrics */
    getApplyBtnMetricsAttrs: PropTypes.func,
};
